import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

interface ImageData {
  url: string;
  width?: number;
  height?: number;
}

interface ImageViewerProps {
  imageUrl: string;
  width?: number;
  height?: number;
  alt?: string;
  className?: string;
  priority?: boolean;
}

export function ImageViewer({ imageUrl, width, height, alt = '', className, priority = false }: ImageViewerProps) {
  const [showModal, setShowModal] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number }>({
    width: width || 0,
    height: height || 0,
  });

  // Load image if dimensions are not provided
  useEffect(() => {
    if (!width || !height) {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
        setLoaded(true);
      };
      img.src = imageUrl;
    } else {
      setLoaded(true);
    }
  }, [imageUrl, width, height]);

  return (
    <>
      <div className="flex justify-start my-0 py-0">
        <img
          src={imageUrl}
          alt={alt}
          className={cn(
            'rounded-lg shadow-md cursor-pointer transition-all duration-200 hover:brightness-95',
            !loaded && 'opacity-0',
            loaded && 'opacity-100',
            className
          )}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          style={{ maxWidth: '65%', margin: 0, padding: 0 }}
          onClick={() => setShowModal(true)}
          onLoad={() => setLoaded(true)}
        />
      </div>

      {/* Image modal */}
      {showModal && loaded && (
        <ImageModal
          image={{ url: imageUrl, ...imageDimensions }}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

interface ImageModalProps {
  image: ImageData;
  onClose: () => void;
}

function ImageModal({ image, onClose }: ImageModalProps) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const calculateDimensions = () => {
      // Get viewport dimensions with padding
      const viewportWidth = window.innerWidth - 32; // 16px padding on each side
      const viewportHeight = window.innerHeight - 32;

      // Use fallback values if dimensions not provided
      const imageWidth = image.width || viewportWidth;
      const imageHeight = image.height || viewportHeight;

      // Calculate aspect ratios
      const imageRatio = imageWidth / imageHeight;
      const viewportRatio = viewportWidth / viewportHeight;

      let width, height;

      if (imageRatio > viewportRatio) {
        // Image is wider than viewport ratio
        width = Math.min(imageWidth, viewportWidth);
        height = width / imageRatio;
      } else {
        // Image is taller than viewport ratio
        height = Math.min(imageHeight, viewportHeight);
        width = height * imageRatio;
      }

      setDimensions({ width, height });
    };

    calculateDimensions();

    // Recalculate on window resize
    window.addEventListener('resize', calculateDimensions);
    return () => window.removeEventListener('resize', calculateDimensions);
  }, [image.width, image.height]);

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-4 z-50 flex items-center justify-center" onClick={onClose}>
        {/* Close button */}
        <button
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
        >
          <X size={24} />
        </button>

        {/* Image container */}
        <div
          className="relative flex items-center justify-center"
          onClick={e => e.stopPropagation()}
        >
          <img
            src={image.url}
            alt=""
            className="max-w-full max-h-full object-contain"
            style={{
              width: dimensions.width,
              height: dimensions.height,
            }}
          />
        </div>
      </div>
    </>,
    document.body
  );
}
